/**
 * Build-time app configuration, so one codebase can build for more than one
 * Firebase project.
 *
 * The browser has no runtime project context the way a Cloud Function does
 * (there is no GCLOUD_PROJECT in a bundle), so these values are baked in at
 * build time from Vite env vars and selected by `--mode` -> `web/.env.<mode>`.
 *
 * THERE ARE NO DEFAULTS, DELIBERATELY.
 *
 * Until 2026-08-10 this config sat in firebase.ts as literals naming the main
 * project. Any fork built from that source signed its users into MAIN's Auth
 * and PUT their uploads into MAIN's Storage bucket. A `?? "<main>"`
 * fallback would preserve exactly that failure — a fork that forgets its env
 * file would build a main-targeting bundle and nothing would say so. So a
 * missing var is a hard error, raised by vite.config.ts before the build starts
 * and again here if a bundle is somehow produced without one.
 */

export const REQUIRED_WEB_ENV_VARS = [
  "VITE_FIREBASE_API_KEY",
  "VITE_FIREBASE_AUTH_DOMAIN",
  "VITE_FIREBASE_PROJECT_ID",
  "VITE_FIREBASE_STORAGE_BUCKET",
  "VITE_FIREBASE_MESSAGING_SENDER_ID",
  "VITE_FIREBASE_APP_ID",
  "VITE_FIREBASE_FUNCTIONS_URL",
] as const;

export interface WebConfig {
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
  };
  /** Base URL for HTTP (onRequest) Cloud Functions in THIS instance. */
  functionsBase: string;
}

/**
 * Pure resolver: env in, config out, throws on anything missing.
 *
 * Pure and env-injected so it is testable from the `functions` package's vitest
 * (web has no runner of its own and must not gain one — see
 * functions/vitest.web.config.ts).
 */
export function resolveWebConfig(env: Record<string, string | undefined>): WebConfig {
  const missing = REQUIRED_WEB_ENV_VARS.filter((k) => {
    const v = env[k];
    // An empty string is unset. A blank var in an env file is a mistake, and
    // treating it as present would produce a silently broken bundle.
    return typeof v !== "string" || v.trim() === "";
  });
  if (missing.length > 0) {
    throw new Error(
      `Web build is missing required Firebase env var(s): ${missing.join(", ")}. ` +
        "Set them in web/.env.<mode> for the instance you are building. " +
        "There is no default: a build that cannot say which project it targets " +
        "must fail rather than silently target another instance.",
    );
  }
  return {
    firebase: {
      apiKey: env.VITE_FIREBASE_API_KEY as string,
      authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string,
      projectId: env.VITE_FIREBASE_PROJECT_ID as string,
      storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET as string,
      messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
      appId: env.VITE_FIREBASE_APP_ID as string,
    },
    functionsBase: (env.VITE_FIREBASE_FUNCTIONS_URL as string).replace(/\/+$/, ""),
  };
}

/**
 * Resolved lazily and memoised, NOT at module load.
 *
 * Eager resolution would throw the moment anything imported this file, which
 * makes the module unimportable anywhere the Vite env is absent — including the
 * `functions` vitest run that tests it. Laziness costs nothing: in a browser
 * bundle the first access happens during app start-up, so a bad build still
 * fails loudly and immediately. The build itself is gated separately and
 * earlier, in vite.config.ts.
 */
let cached: WebConfig | null = null;

export function getWebConfig(): WebConfig {
  if (cached === null) {
    cached = resolveWebConfig(
      import.meta.env as unknown as Record<string, string | undefined>,
    );
  }
  return cached;
}

/** Firebase client config for THIS instance. */
export function firebaseConfig(): WebConfig["firebase"] {
  return getWebConfig().firebase;
}

/** Base URL for HTTP (onRequest) Cloud Functions. Use instead of a literal. */
export function functionsBase(): string {
  return getWebConfig().functionsBase;
}

/** Full URL of one HTTP function in this instance. */
export function functionsUrl(name: string): string {
  return `${functionsBase()}/${name}`;
}

/**
 * The callback / ACS URL an identity provider must be configured with.
 *
 * Derived from the instance's OWN auth domain. `pages/admin/AdminSso.tsx`
 * printed the VENDOR's auth domain as a literal, so every other instance told
 * its administrator to configure a URL belonging to somebody else's project.
 * Wrong in a place where a person is being asked to type it into a third-party
 * system, and silent with it: SSO would simply not work, and the instruction
 * would look authoritative while being wrong.
 *
 * The literal is deliberately not repeated here — the guard in
 * appConfig.test.ts sweeps this whole tree for instance identifiers, and it
 * caught this very comment when it did name it. That is the guard working.
 */
export function ssoCallbackUrlFor(authDomain: string): string {
  return `https://${authDomain}/__/auth/handler`;
}

export function ssoCallbackUrl(): string {
  return ssoCallbackUrlFor(firebaseConfig().authDomain);
}
